import React, { useEffect, useRef, useState } from 'react';
import MonacoEditor from 'react-monaco-editor';
import { coreUtils } from 'dealer-website-components';

import FileUploader from './WysiwygEditorFileUploader';
import WysiwygEditorVariables from './WysiwygEditorVariables';

const EDITOR_THEME = 'builder-navy';
const EDITOR_BACKGROUND = '#1F2640';
const VARIABLE_SEARCH = '\\{\\{\\s*[\\w.]+(?:\\s*:\\s*[^{}]+?)?\\s*\\}\\}';
const VARIABLE_PATTERN = /\{\{\s*([\w.]+)(?:\s*:\s*([^{}]+?))?\s*\}\}/g;

const devices = [
  { key: 'desktop', label: 'Desktop', icon: 'fal fa-desktop' },
  { key: 'tablet', label: 'Tablet', icon: 'fal fa-tablet-alt', width: 778 },
  { key: 'mobile', label: 'Mobile', icon: 'fal fa-mobile-alt', width: 375 }
];

const options = {
  fontSize: 13,
  lineHeight: 22,
  fontFamily: "'SF Mono', Menlo, Consolas, monospace",
  minimap: { enabled: false },
  wordWrap: 'on',
  wrappingIndent: 'indent',
  scrollBeyondLastLine: false,
  automaticLayout: true,
  padding: { top: 12, bottom: 12 },
  selectOnLineNumbers: true,
  colorDecorators: true,
  overviewRulerLanes: 0,
  hideCursorInOverviewRuler: true,
  scrollbar: { verticalScrollbarSize: 10, horizontalScrollbarSize: 10 }
};

const toolbarButtonClassNames =
  'builder-flex builder-items-center builder-gap-1.5 builder-h-8 builder-px-2.5 builder-text-[13px] builder-font-medium builder-text-slate-300 builder-rounded-md hover:builder-bg-white/10 hover:builder-text-white focus:builder-outline-none focus-visible:builder-ring-2 focus-visible:builder-ring-[#9DB2FF]';

const defineEditorTheme = (monaco) => {
  monaco.editor.defineTheme(EDITOR_THEME, {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: '', foreground: 'E2E8F0' },
      { token: 'tag', foreground: '9DB2FF' },
      { token: 'metatag', foreground: '9DB2FF' },
      { token: 'delimiter', foreground: '8590B5' },
      { token: 'attribute.name', foreground: 'E9C98B' },
      { token: 'attribute.value', foreground: '9FD3C1' },
      { token: 'string', foreground: '9FD3C1' },
      { token: 'comment', foreground: '8590B5', fontStyle: 'italic' }
    ],
    colors: {
      'editor.background': EDITOR_BACKGROUND,
      'editor.foreground': '#E2E8F0',
      'editor.lineHighlightBackground': '#262F50',
      'editor.lineHighlightBorder': '#262F50',
      'editor.selectionBackground': '#3B4878',
      'editorCursor.foreground': '#E2E8F0',
      'editorLineNumber.foreground': '#8590B5',
      'editorLineNumber.activeForeground': '#E2E8F0',
      'editorBracketMatch.background': '#3B487880',
      'editorBracketMatch.border': '#00000000',
      'editorIndentGuide.background': '#2D375A',
      'editorIndentGuide.activeBackground': '#3B4878',
      'editorWidget.background': '#262F50',
      'editorSuggestWidget.background': '#262F50',
      'scrollbarSlider.background': '#FFFFFF1A',
      'scrollbarSlider.hoverBackground': '#FFFFFF33',
      'scrollbarSlider.activeBackground': '#FFFFFF4D'
    }
  });
};

const resolvePreviewHtml = (html, scope) => {
  const htmlVariables = coreUtils?.htmlVariables;
  if (!html || !htmlVariables) return html;

  const now = new Date();
  return html.replace(VARIABLE_PATTERN, (token, name, format) => (htmlVariables[name] ? htmlVariables[name].resolve({ now, ...scope, format }) || '' : token));
};

export default function WysiwygEditor(props) {
  const [deviceType, setDeviceType] = useState('desktop');
  const [cursor, setCursor] = useState({ lineNumber: 1, column: 1 });
  const [stageWidth, setStageWidth] = useState(0);

  const eRef = useRef();
  const stageRef = useRef();
  const variableDecorations = useRef([]);

  useEffect(() => {
    if (!stageRef.current || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(([entry]) => setStageWidth(entry.contentRect.width));
    observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, []);

  const highlightVariables = (editor) => {
    const model = editor.getModel();
    if (!model) return;

    const matches = model.findMatches(VARIABLE_SEARCH, false, true, false, null, false);
    variableDecorations.current = editor.deltaDecorations(
      variableDecorations.current,
      matches.map((match) => ({ range: match.range, options: { inlineClassName: 'wysiwyg-editor-variable' } }))
    );
  };

  const handleEditorDidMount = (editor) => {
    highlightVariables(editor);
    editor.onDidChangeModelContent(() => highlightVariables(editor));
    editor.onDidChangeCursorPosition((event) => setCursor(event.position));
  };

  const formatDocument = () => {
    eRef.current?.editor?.getAction('editor.action.formatDocument')?.run();
  };

  const device = devices.find((d) => d.key === deviceType);
  const scale = device.width && stageWidth ? Math.min(1, stageWidth / device.width) : 1;
  const widthLabel = device.width ? `${device.width} px${scale < 1 ? `, scaled to ${Math.round(scale * 100)}%` : ''}` : 'Full width';
  const previewHtml = props.showVariables ? resolvePreviewHtml(props.data, props.variableScope) : props.data;

  return (
    <div className='builder-flex builder-flex-col builder-w-full builder-bg-white builder-border builder-border-slate-200 builder-rounded-lg builder-overflow-hidden'>
      <div className='builder-grid builder-grid-cols-2' style={{ height: '70vh', minHeight: 560 }}>
        <div className='builder-flex builder-flex-col builder-min-w-0' style={{ backgroundColor: EDITOR_BACKGROUND }}>
          <div className='builder-flex builder-items-center builder-justify-between builder-h-12 builder-pl-4 builder-pr-3 builder-border-b builder-border-white/10'>
            <div className='builder-flex builder-items-center builder-gap-2'>
              <span className='builder-text-sm builder-font-semibold builder-text-slate-200'>HTML</span>
              <span className='builder-text-[13px]' style={{ color: '#8590B5' }}>
                Line {cursor.lineNumber}, column {cursor.column}
              </span>
            </div>
            {!props.readOnly && (
              <div className='builder-flex builder-items-center builder-gap-1'>
                <button type='button' onClick={formatDocument} className={toolbarButtonClassNames}>
                  <i className='fal fa-align-left' />
                  Format
                </button>
                <FileUploader editorRef={eRef} buttonClassName={toolbarButtonClassNames} />
              </div>
            )}
          </div>
          <div className='builder-flex-1 builder-min-h-0'>
            <MonacoEditor
              ref={eRef}
              language='html'
              theme={EDITOR_THEME}
              value={props.data || undefined}
              defaultValue={'<div>Hello there! Add your HTML here</div>'}
              editorWillMount={defineEditorTheme}
              editorDidMount={handleEditorDidMount}
              onChange={(newValue) => {
                props.onChange(newValue);
              }}
              options={{ ...options, readOnly: props.readOnly }}
            />
          </div>
        </div>

        <div className='builder-flex builder-flex-col builder-min-w-0 builder-border-l builder-border-slate-200'>
          <div className='builder-flex builder-items-center builder-justify-between builder-gap-3 builder-h-12 builder-px-4 builder-border-b builder-border-slate-200'>
            <span className='builder-text-sm builder-font-semibold builder-text-slate-800'>Preview</span>
            <div className='builder-flex builder-items-center builder-gap-3'>
              <span className='builder-text-[13px] builder-text-slate-500 builder-tabular-nums'>{widthLabel}</span>
              <div role='group' aria-label='Preview width' className='builder-flex builder-p-0.5 builder-bg-slate-100 builder-rounded-lg'>
                {devices.map((d) => {
                  const isActive = d.key === deviceType;
                  return (
                    <button
                      key={d.key}
                      type='button'
                      aria-pressed={isActive}
                      onClick={() => setDeviceType(d.key)}
                      className={`builder-flex builder-items-center builder-gap-1.5 builder-h-[30px] builder-px-2.5 builder-text-[13px] builder-font-medium builder-rounded-md builder-transition-colors focus:builder-outline-none focus-visible:builder-ring-2 focus-visible:builder-ring-primary ${
                        isActive ? 'builder-bg-white builder-text-slate-800 builder-shadow-sm' : 'builder-text-slate-500 hover:builder-text-slate-800'
                      }`}
                    >
                      <i className={d.icon} />
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className='builder-flex-1 builder-min-h-0 builder-overflow-auto builder-bg-slate-100 builder-p-6'>
            <div ref={stageRef} className='builder-flex builder-justify-center'>
              <div
                className={`builder-bg-white builder-border builder-border-slate-200 builder-rounded-md builder-overflow-hidden builder-transition-[width] builder-duration-200 motion-reduce:builder-transition-none ${
                  device.width ? 'builder-shadow-lg' : ''
                }`}
                style={{ width: device.width || '100%', zoom: scale }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </div>
      </div>
      {props.showVariables && <WysiwygEditorVariables showCampaignVariables={props.showCampaignVariables} />}
    </div>
  );
}
